import { useEffect, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Navigate, useLocation } from 'react-router-dom'

import { Button } from '../../ui/Button/Button'
import { Card } from '../../ui/Card/Card'
import { Spinner } from '../../ui/Spinner/Spinner'
import { useAuth } from '../auth/AuthProvider'
import type { DashboardUser } from '../auth/auth-adapter'
import {
  getOnboardingApiClient,
  OnboardingApiError,
  type OnboardingApiClient,
} from './onboarding-api'
import {
  onboardingStatusQueryOptions,
  onboardingProvisioningProgressQueryKey,
  onboardingProvisioningProgressQueryOptions,
  onboardingValidationQueryKey,
} from './onboarding-query'
import { destinationPath, routeOnboardingResponse } from './onboarding-routing'
import styles from './OnboardingLayout.module.css'

export function ProvisioningPage({
  client = getOnboardingApiClient(),
}: {
  client?: OnboardingApiClient
}) {
  const auth = useAuth()
  if (auth.status !== 'authenticated') {
    return <Spinner label="Restoring your setup" />
  }
  return <AuthenticatedProvisioningPage client={client} user={auth.user} />
}

function AuthenticatedProvisioningPage({
  client,
  user,
}: {
  client: OnboardingApiClient
  user: DashboardUser
}) {
  const auth = useAuth()
  const location = useLocation()
  const queryClient = useQueryClient()
  const query = useQuery(onboardingStatusQueryOptions(client, user))
  const completionAttemptedRevision = useRef<number | null>(null)
  const validating =
    query.data !== undefined &&
    'status' in query.data &&
    query.data.status === 'VALIDATING'
  const validationRevision =
    validating && query.data && 'revision' in query.data
      ? query.data.revision
      : 0
  const validationQuery = useQuery({
    queryKey: onboardingValidationQueryKey(user.uid),
    enabled: validating,
    queryFn: async () => {
      if (!client.getValidation || !client.startValidation) {
        throw new Error('Validation API is unavailable')
      }
      const current = await client.getValidation(user)
      if (current.status !== 'PENDING') return current
      return client.startValidation(user, validationRevision)
    },
    staleTime: 0,
  })
  const activation = useMutation({
    mutationFn: async () => {
      if (!client.completeActivation) {
        throw new Error('Activation API is unavailable')
      }
      return client.completeActivation(user, validationRevision)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['onboarding', 'status', user.uid],
      })
    },
  })
  const provisioningCompletion = useMutation({
    mutationFn: (expectedRevision: number) =>
      client.completeProvisioning(user, expectedRevision),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['onboarding', 'status', user.uid],
      })
      await queryClient.invalidateQueries({
        queryKey: onboardingProvisioningProgressQueryKey(user.uid),
      })
    },
  })
  const provisioningStart = useMutation({
    mutationFn: (expectedRevision: number) =>
      client.startProvisioning(user, expectedRevision),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: onboardingProvisioningProgressQueryKey(user.uid),
      })
    },
  })
  const provisioning =
    query.data !== undefined &&
    'status' in query.data &&
    query.data.status === 'PROVISIONING'
  const provisioningRevision =
    provisioning && query.data && 'revision' in query.data
      ? query.data.revision
      : 0
  const progressQuery = useQuery({
    ...onboardingProvisioningProgressQueryOptions(client, user),
    enabled: provisioning,
  })
  useEffect(() => {
    if (provisioning && provisioningStart.status === 'idle') {
      provisioningStart.mutate(provisioningRevision)
    }
  }, [provisioning, provisioningRevision, provisioningStart])
  useEffect(() => {
    if (
      provisioning &&
      progressQuery.data?.status === 'READY_FOR_VALIDATION' &&
      !provisioningCompletion.isPending &&
      completionAttemptedRevision.current !== provisioningRevision
    ) {
      completionAttemptedRevision.current = provisioningRevision
      provisioningCompletion.mutate(provisioningRevision)
    }
  }, [
    provisioning,
    provisioningCompletion,
    provisioningRevision,
    progressQuery.data?.status,
  ])
  useEffect(() => {
    if (
      validating &&
      validationQuery.data?.status === 'PASSED' &&
      activation.status === 'idle'
    ) {
      activation.mutate()
    }
  }, [activation, validating, validationQuery.data?.status])

  if (query.isPending) return <Spinner label="Checking your setup" />
  if (query.isError) {
    if (
      query.error instanceof OnboardingApiError &&
      query.error.code === 'unauthorized'
    ) {
      return (
        <Navigate
          to="/login"
          replace
          state={{ from: location.pathname, reauthenticate: true }}
        />
      )
    }
    return (
      <Card className={styles.panel}>
        <h1>Setup status could not be loaded</h1>
        <p>Retry to restore your authoritative onboarding state.</p>
        <Button type="button" onClick={() => void query.refetch()}>
          Retry
        </Button>
      </Card>
    )
  }

  const destination = routeOnboardingResponse(query.data)
  if (destination !== 'provisioning') {
    return <Navigate to={destinationPath(destination)} replace />
  }
  const isValidating =
    'status' in query.data && query.data.status === 'VALIDATING'

  if (isValidating) {
    const validation = validationQuery.data
    const checking =
      validationQuery.isPending || validation?.status === 'RUNNING'
    const passed = validation?.status === 'PASSED' && !activation.isError
    const needsAttention =
      validationQuery.isError ||
      activation.isError ||
      (validation !== undefined &&
        ['FAILED', 'RECONCILIATION_REQUIRED'].includes(validation.status))
    return (
      <Card className={styles.panel}>
        <h1>
          {checking
            ? 'Flio is checking your clinic setup'
            : passed
              ? 'Flio is finishing your clinic setup'
              : 'Flio is reviewing your clinic setup'}
        </h1>
        <p>
          {passed
            ? 'Validation passed. Flio is activating only your approved Communications services and verifying them.'
            : needsAttention
              ? 'Your setup needs attention before Communications can be activated. Flio is reviewing it.'
              : 'Your complete Communications setup is being checked. No action is needed right now.'}
        </p>
        {validation && (
          <p className={styles.status}>
            Messaging: {validation.messaging.replace('_', ' ').toLowerCase()}
            {' · '}Voice: {validation.voice.replace('_', ' ').toLowerCase()}
          </p>
        )}
        <p className={styles.status}>Communications are not active yet.</p>
        <Button
          type="button"
          variant="secondary"
          onClick={() => void auth.logout()}
        >
          Sign out
        </Button>
      </Card>
    )
  }

  const progress = progressQuery.data?.status
  const provisioningNeedsAttention =
    provisioningStart.isError ||
    progressQuery.isError ||
    progress === 'RETRYABLE_FAILURE' ||
    progress === 'RECONCILIATION_REQUIRED' ||
    progress === 'TERMINAL_FAILURE' ||
    provisioningCompletion.isError
  const progressCopy =
    progress === 'WAITING_FOR_NUMBER'
      ? 'A Communications number is being assigned. Operator action may be required.'
      : progress === 'CONFIGURING_TWILIO'
        ? 'Secure messaging and calling are being configured.'
        : progress === 'CONFIGURING_VAPI_ASSISTANT'
          ? 'Your AI voice assistant is being prepared.'
          : progress === 'CONFIGURING_VAPI_DESTINATION'
            ? 'Your AI voice routing is being prepared.'
            : progress === 'PROJECTING_SMS_RUNTIME'
              ? 'Your messaging runtime is being prepared.'
              : progress === 'PROJECTING_VOICE_RUNTIME'
                ? 'Your voice runtime is being prepared.'
                : progress === 'RETRYABLE_FAILURE'
                  ? 'A temporary setup problem occurred. Flio can retry safely.'
                  : progress === 'RECONCILIATION_REQUIRED'
                    ? 'Your setup needs operator review before it can continue.'
                    : progress === 'TERMINAL_FAILURE'
                      ? 'Your setup needs operator attention before it can continue.'
                      : progress === 'READY_FOR_VALIDATION'
                        ? 'Provisioning is complete. Flio is starting final validation.'
                        : 'Your approved setup is being prepared.'
  return (
    <Card className={styles.panel}>
      <h1>
        {provisioningNeedsAttention
          ? 'Flio is reviewing your clinic setup'
          : 'Flio is preparing your clinic setup'}
      </h1>
      <p>
        {provisioningNeedsAttention
          ? 'Your setup needs operator attention before provisioning can continue.'
          : progressCopy}
      </p>
      <p className={styles.status}>
        Communications are not active yet. No provider details are exposed here.
      </p>
      <Button
        type="button"
        variant="secondary"
        onClick={() => void auth.logout()}
      >
        Sign out
      </Button>
    </Card>
  )
}
